import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { cn } from '../../utils/cn';

export interface TabOption {
	id: string;
	label: string;
	icon?: IconDefinition;
	disabled?: boolean;
}

interface TabsProps {
	tabs: TabOption[];
	activeTab: string;
	onTabChange: (tabId: string) => void;
	variant?: 'default' | 'pills' | 'underline';
	alignment?: 'left' | 'center' | 'right';
	showIcons?: boolean;
	className?: string;
}

export const Tabs: React.FC<TabsProps> = ({
	tabs,
	activeTab,
	onTabChange,
	variant = 'default',
	alignment = 'left',
	showIcons = false,
	className,
}) => {
	const colors = useThemeColors();
	const alignmentClasses = {
		left: 'justify-start',
		center: 'justify-center',
		right: 'justify-end',
	};

	const getTabClasses = (tab: TabOption, isActive: boolean) => {
		const baseClasses = cn(
			'px-4 py-2 font-medium text-sm transition-colors duration-150',
			colors.classes.focusRingPrimary,
			tab.disabled && 'opacity-50 cursor-not-allowed',
		);

		switch (variant) {
			case 'pills':
				return cn(
					baseClasses,
					'rounded-md',
					isActive
						? cn(colors.classes.bgPrimary, colors.classes.textWhite)
						: cn(
								colors.classes.textPrimary,
								'hover:' + colors.classes.bgSecondary,
							),
				);
			case 'underline':
				return cn(
					baseClasses,
					'border-b-2',
					isActive
						? cn(
								'border-blue-500 dark:border-blue-400',
								colors.classes.textPrimary,
							)
						: cn(
								'border-transparent',
								colors.classes.textPrimary,
								'hover:opacity-80 hover:border-gray-300 dark:hover:border-gray-600',
							),
				);
			default:
				return cn(
					baseClasses,
					'border-b-2',
					isActive
						? cn(
								'border-blue-500 dark:border-blue-400',
								colors.classes.textPrimary,
							)
						: cn(
								'border-transparent',
								colors.classes.textSecondary,
								'hover:' + colors.classes.textPrimary,
							),
				);
		}
	};

	return (
		<div className={cn('w-full', className)}>
			<div
				role="tablist"
				className={cn(
					'flex',
					alignmentClasses[alignment],
					variant === 'underline' &&
						cn('border-b', colors.classes.borderDefault),
				)}
			>
				{tabs.map((tab) => (
					<button
						key={tab.id}
						type="button"
						// role="tab" + aria-selected aligns with the WAI-ARIA tab
						// pattern. Without these, screen readers announce the tab
						// strip as a generic group of buttons, and tests using
						// `getByRole('tab', ...)` (the conventional accessible
						// selector) can't find tabs at all.
						role="tab"
						aria-selected={activeTab === tab.id}
						aria-disabled={tab.disabled === true ? true : undefined}
						onClick={() => !tab.disabled && onTabChange(tab.id)}
						className={getTabClasses(tab, activeTab === tab.id)}
						disabled={tab.disabled}
					>
						{/* PD-14355: plain spans so the label inherits the button's
						    text-sm. The previous nested <Text> defaulted to text-base,
						    overriding the intended size and making every tab label
						    across the app look oversized/inconsistent. */}
						<span className="flex items-center space-x-2">
							{showIcons && tab.icon && (
								<FontAwesomeIcon icon={tab.icon} className="w-4 h-4" />
							)}
							<span>{tab.label}</span>
						</span>
					</button>
				))}
			</div>
		</div>
	);
};
